import * as listaEnviosService from '../components/servicios/listaEnvioService.js'
import { insertar } from '../components/servicios/mensajesService.js'
import * as notificacionesService from '../components/servicios/notificacionesService.js'
import * as publicacionesService from '../components/servicios/publicacionesService.js'
import * as tiendasOficialesService from '../components/servicios/tiendasOficialesService.js'
import {
  MENSAJE_TIPO_AVISO_PROXIMO_A_VENCER,
  MENSAJE_TIPO_LISTA_ENVIO_VENCIDA,
  MENSAJE_TIPO_PUBLICACION_VENCIDA,
  MENSAJE_TIPO_TIENDA_OFICIAL
} from '../constants/mensajesTipos.js'
import {
  DIAS_AVISO_VENCIMIENTO,
  ESTADO_PUBLICADA,
  ESTADO_VENCIDA as PUBLICACION_ESTADO_VENCIDA,
  mensajeAvisoProximoAVencer
} from '../constants/publicaciones.js'

import { ESTADO_VENCIDA as TIENDA_ESTADO_VENCIDA } from '../constants/tiendaOficial.js'
import { avisoProximoAVencer, fechaVenceEnDias, validarRangoFechas } from './dateValidation.js'
import { toastErrorLog } from './toastErrorLog.js'

const CINCO_DIAS = 5

const buscarNotificacionesRecientes = async (idUsuario, idMensajeTipo, dias) => {
  const { data } = await notificacionesService.buscarPorMensaje(idUsuario)
  const notificaciones = Array.isArray(data?.notificaciones) ? data.notificaciones : []

  const ahora = new Date()
  const ventanaInicio = new Date(ahora)
  ventanaInicio.setDate(ventanaInicio.getDate() - dias)

  return notificaciones.filter(
    (n) =>
      n.idMensajeTipo === idMensajeTipo &&
      validarRangoFechas(ventanaInicio, n.fechaEscrito, 'day') &&
      validarRangoFechas(n.fechaEscrito, ahora, 'day')
  )
}

export const avisosProximosAVencer = async (data, idUsuario) => {
  try {
    const proximas = data.filter(
      (p) =>
        p.idPublicacionEstado === ESTADO_PUBLICADA &&
        avisoProximoAVencer(p.fechaVencimientoAviso, DIAS_AVISO_VENCIMIENTO)
    )

    if (!proximas.length) {
      return false
    }

    const notificadas = await buscarNotificacionesRecientes(
      idUsuario,
      MENSAJE_TIPO_AVISO_PROXIMO_A_VENCER,
      DIAS_AVISO_VENCIMIENTO
    )

    const yaNotificadas = new Set(notificadas.map((n) => n.idPublicacion))

    const pendientes = proximas.filter((p) => !yaNotificadas.has(p.idPublicacion))

    if (!pendientes.length) {
      return false
    }

    await Promise.all(
      pendientes.map((p) =>
        notificar(
          idUsuario,
          mensajeAvisoProximoAVencer(p.titulo),
          MENSAJE_TIPO_AVISO_PROXIMO_A_VENCER,
          p.idPublicacion
        )
      )
    )
    return true
  } catch (error) {
    const mensaje = `Ocurrió un error al verificar los avisos próximos a vencer.`
    toastErrorLog(error, mensaje, 'venta')
    return false
  }
}

export const publicacionesVencidas = async (data, idUsuario) => {
  try {
    const vencidas = data.filter((p) => {
      if (!p.hasta) {
        return false
      }

      const estaVigente = validarRangoFechas(new Date(), p.hasta, 'day')
      return !estaVigente && p.idPublicacionEstado === ESTADO_PUBLICADA
    })

    if (vencidas.length > 0) {
      await Promise.all(
        vencidas.map((p) =>
          publicacionesService.actualizarEstado(
            p.idPublicacion,
            p.idVendedor,
            PUBLICACION_ESTADO_VENCIDA
          )
        )
      )
      const inicio =
        vencidas.length > 1
          ? 'Tenés publicaciones vencidas. Las publicaciones'
          : 'Tenés una publicació vencida. La publicación'
      await notificar(
        idUsuario,
        `${inicio} ${vencidas.map((p) => p.idPublicacion).join(', ')}  han sido marcadas con estado "Vencida" debido a su vigencia. Por favor, revisalas en Ventas -> Administrar para reactivarlas o modificarlas`,
        MENSAJE_TIPO_PUBLICACION_VENCIDA
      )
      return true
    }
    return false
  } catch (error) {
    const mensaje = `Ocurrió un error al verificar publicaciones vencidas.`
    toastErrorLog(error, mensaje, 'venta')
    return false
  }
}

export const tiendaOficialVencimiento = async (tienda, idUsuario, publicaciones) => {
  try {
    const fechaHasta = tienda?.fechaHasta
    if (!fechaHasta) {
      return false
    }

    const { data } = await notificacionesService.buscarPorMensaje(idUsuario)
    const notificaciones = Array.isArray(data?.notificaciones) ? data.notificaciones : []
    const ventanaInicio = new Date(fechaHasta)
    ventanaInicio.setDate(ventanaInicio.getDate() - CINCO_DIAS)
    const yaNotifico = notificaciones.some(
      (n) =>
        n.idMensajeTipo === MENSAJE_TIPO_TIENDA_OFICIAL &&
        validarRangoFechas(ventanaInicio, n.fechaEscrito, 'day')
    )

    if (!validarRangoFechas(new Date(), fechaHasta, 'day')) {
      const idsExcluidos = publicaciones.map((p) => p.idPublicacion)
      await publicacionesService.actualizarExcluidosDeTiendaOficial(tienda.empid, idsExcluidos)
      await tiendasOficialesService.actualizarTienda({
        ...tienda,
        idTiendaOficialEstado: TIENDA_ESTADO_VENCIDA
      })
    } else if (fechaVenceEnDias(fechaHasta, CINCO_DIAS) && !yaNotifico) {
      await notificar(
        idUsuario,
        `Tu tienda oficial "${tienda.nombreTienda}" tiene fecha de vencimiento próxima. Por favor, revisá tu tienda oficial para reactivarla o modificarla en el menú Ventas->Convertir a tienda oficial.`,
        MENSAJE_TIPO_TIENDA_OFICIAL
      )
      return true
    }
    return false
  } catch (error) {
    const mensaje = `Ocurrió un error al verificar el vencimiento de la tienda oficial.`
    toastErrorLog(error, mensaje, 'venta')
    return false
  }
}

export const listaEnviosVencidas = async (listaEnvios, idUsuario) => {
  const vencidas = listaEnvios.filter((listaEnvio) => {
    if (!listaEnvio.vigenciaHasta || listaEnvio.grilla[0].vencida) {
      return false
    }
    const ahora = new Date().toISOString()
    const vigenciaHasta = new Date(listaEnvio.vigenciaHasta).toISOString()

    return new Date(ahora) > new Date(vigenciaHasta)
  })

  if (vencidas.length > 0) {
    const lista = [
      ...vencidas.map((listaEnvio) => {
        return {
          idLista: listaEnvio.idLista,
          nombreLista: listaEnvio.nombreLista,
          nombreVendedor: listaEnvio.nombreVendedor,
          vencida: true
        }
      })
    ]

    listaEnviosService.actualizar(lista)
    await notificar(
      idUsuario,
      `Las listas de envío ${vencidas.map((p) => `"${p.nombreLista}"`).join(', ')} han sido marcadas como "Vencida" debido a su vigencia. Por favor, revisalas en Ventas -> Listas envío para reactivarlas. Además, recordá controlar las publicaciones que tengan asignadas dichas listas`,
      MENSAJE_TIPO_LISTA_ENVIO_VENCIDA
    )
    return true
  }

  return false
}

const notificar = async (idUsuario, msg, idMensajeTipo, idPublicacion = null) => {
  try {
    const mensaje = {
      idMensajePadre: null,
      idPublicacion,
      idPedido: null,
      idPresupuesto: null,
      idVendedor: idUsuario,
      idUsuarioChat: idUsuario,
      idUsuarioEmisor: idUsuario,
      texto: msg,
      privado: true,
      idAdjunto: null,
      idMensajeTipo: idMensajeTipo
    }
    await insertar(mensaje)
  } catch (error) {
    const mensaje = `Ocurrió un error al enviar la notificación.`
    toastErrorLog(error, mensaje, 'venta')
  }
}
